import { useNavigationStore, useNavigationSelectors } from '../navigationStore';

/**
 * Custom hook for navigation management
 * Provides convenient access to navigation state and actions
 */
export const useNavigation = () => {
  const {
    view,
    source,
    tab,
    searchItems,
    hasValidParams,
    selectedInfoItem,
    geneContent,
    mappingContent,
    setView,
    setSource,
    setTab,
    setSearchItems,
    setHasValidParams,
    setSelectedInfoItem,
    setGeneContent,
    setMappingContent,
    resetNavigation,
    updateSearchState,
    clearSidebarContent,
  } = useNavigationStore();

  const {
    isInSearchContext,
    hasSearchResults,
    hasSidebarContent,
    getCurrentSearchContext,
    getSidebarState,
    isViewActive,
    isSourceActive,
    isTabActive,
    getNavigationBreadcrumb,
  } = useNavigationSelectors();

  return {
    // State
    navigation: {
      view,
      source,
      tab,
      searchItems,
      hasValidParams,
      selectedInfoItem,
      geneContent,
      mappingContent,
    },

    // Computed values
    isInSearchContext,
    hasSearchResults,
    hasSidebarContent,
    getCurrentSearchContext,
    getSidebarState,
    getNavigationBreadcrumb,

    // Actions
    setView,
    setSource,
    setTab,
    setSearchItems,
    setHasValidParams,
    setSelectedInfoItem,
    setGeneContent,
    setMappingContent,
    resetNavigation,
    updateSearchState,
    clearSidebarContent,

    // Convenience methods
    isViewActive,
    isSourceActive,
    isTabActive,

    // Navigation helpers
    navigateToSearch: (view: string, source: string, tab: string = '') => {
      updateSearchState(view, source, tab, [], false);
    },

    updateSearchResults: (items: any[], hasValid: boolean = true) => {
      setSearchItems(items);
      setHasValidParams(hasValid);
    },

    activateGeneContent: (symbol: string, id: number | null, name: string) => {
      setGeneContent({
        isActive: true,
        geneInfo: { symbol, id, name },
      });
    },

    deactivateGeneContent: () => {
      setGeneContent({
        isActive: false,
        geneInfo: { symbol: '', id: null, name: '' },
      });
    },

    activateMappingContent: () => {
      setMappingContent({ isActive: true });
    },

    deactivateMappingContent: () => {
      setMappingContent({ isActive: false });
    },

    // Check if we're on a specific page/view
    isOnSearchPage: () => isInSearchContext,
    isOnResultsPage: () => hasSearchResults && hasValidParams,
    
    // Get current page context
    getCurrentPageContext: () => ({
      ...getCurrentSearchContext(),
      sidebar: getSidebarState(),
      breadcrumb: getNavigationBreadcrumb(),
    }),
  };
};

/**
 * Hook for search-specific navigation
 * Useful when you only need search-related navigation state
 */
export const useSearchNavigation = () => {
  const view = useNavigationStore((state) => state.view);
  const source = useNavigationStore((state) => state.source);
  const tab = useNavigationStore((state) => state.tab);
  const searchItems = useNavigationStore((state) => state.searchItems);
  const hasValidParams = useNavigationStore((state) => state.hasValidParams);
  const setView = useNavigationStore((state) => state.setView);
  const setSource = useNavigationStore((state) => state.setSource);
  const setTab = useNavigationStore((state) => state.setTab);
  const updateSearchState = useNavigationStore((state) => state.updateSearchState);

  return {
    view,
    source,
    tab,
    searchItems,
    hasValidParams,
    setView,
    setSource,
    setTab,
    updateSearchState,
    
    // Convenience methods
    hasSearchContext: view !== '' && source !== '',
    hasResults: searchItems.length > 0,
    isValidSearch: hasValidParams && searchItems.length > 0,
  };
};

/**
 * Hook for sidebar navigation
 * Useful when you only need sidebar-related state
 */
export const useSidebarNavigation = () => {
  const selectedInfoItem = useNavigationStore((state) => state.selectedInfoItem);
  const geneContent = useNavigationStore((state) => state.geneContent);
  const mappingContent = useNavigationStore((state) => state.mappingContent);
  const setSelectedInfoItem = useNavigationStore((state) => state.setSelectedInfoItem);
  const setGeneContent = useNavigationStore((state) => state.setGeneContent);
  const setMappingContent = useNavigationStore((state) => state.setMappingContent);
  const clearSidebarContent = useNavigationStore((state) => state.clearSidebarContent);

  return {
    selectedInfoItem,
    geneContent,
    mappingContent,
    setSelectedInfoItem,
    setGeneContent,
    setMappingContent,
    clearSidebarContent,
    
    // Computed values
    hasContent: selectedInfoItem !== null || geneContent.isActive || mappingContent.isActive,
    isGeneActive: geneContent.isActive,
    isMappingActive: mappingContent.isActive,
    hasSelectedItem: selectedInfoItem !== null,
  };
};

/**
 * Hook for navigation breadcrumb
 * Useful for displaying current navigation path
 */
export const useNavigationBreadcrumb = () => {
  const { getNavigationBreadcrumb } = useNavigationSelectors();
  const { view, source, tab } = useNavigationStore();

  return {
    breadcrumb: getNavigationBreadcrumb(),
    currentPath: { view, source, tab },
    
    // Formatted breadcrumb for display
    getFormattedBreadcrumb: (separator: string = ' > ') => 
      getNavigationBreadcrumb().join(separator),
    
    // Check if we're at a specific depth
    isAtDepth: (depth: number) => getNavigationBreadcrumb().length === depth,
    
    // Get parent path (one level up)
    getParentPath: () => {
      const breadcrumb = getNavigationBreadcrumb();
      return breadcrumb.slice(0, -1);
    },
  };
};